import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';

const API = process.env.REACT_APP_BACKEND_URL;
const COLORS = { primary: '#2b3380', whatsapp: '#25D366', success: '#4caf50', warning: '#ff9800' };

function WhatsAppWidget({ user, onLogout }) {
  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState(null);
  const [embedCode, setEmbedCode] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  const [saving, setSaving] = useState(false);
  
  const [formData, setFormData] = useState({
    company_name: '',
    welcome_message: 'Hi! How can we help you today?',
    phone_number: '',
    position: 'bottom-right',
    primary_color: '#25D366',
    button_text: 'Chat with us',
    show_agent_name: true,
    agent_name: 'Support Team',
    agent_avatar: '',
    business_hours: 'Mon-Fri 9AM-6PM',
    is_active: true
  });

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/whatsapp/widget/config`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.config) {
        setConfig(response.data.config);
        setFormData(response.data.config);
        setEmbedCode(response.data.embed_code);
      }
      setLoading(false);
    } catch (error) {
      console.error('Error fetching config:', error);
      setLoading(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/api/whatsapp/widget/config`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setEmbedCode(response.data.embed_code);
      alert('✅ Widget configuration saved!');
      fetchConfig();
    } catch (error) {
      console.error('Error saving config:', error);
      alert('Error saving configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const copyEmbedCode = () => {
    navigator.clipboard.writeText(embedCode);
    alert('✅ Embed code copied to clipboard!');
  };

  if (loading) {
    return (
      <Layout user={user} onLogout={onLogout}>
        <div className="flex items-center justify-center h-screen">
          <div className="text-center">
            <div className="text-6xl mb-4">💬</div>
            <p className="text-xl text-gray-600">Loading Widget Settings...</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="p-6 bg-gray-50 min-h-screen">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold mb-2" style={{ color: COLORS.primary }}>
            💬 WhatsApp Website Chat Widget
          </h1>
          <p className="text-gray-600">
            Add a WhatsApp chat widget to your website and connect with visitors instantly
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Configuration Form */}
          <div className="bg-white rounded-lg shadow-lg p-6">
            <h2 className="text-2xl font-semibold mb-4" style={{ color: COLORS.primary }}>
              ⚙️ Widget Configuration
            </h2>
            
            <form onSubmit={handleSave} className="space-y-4">
              {/* Company Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Company Name *
                </label>
                <input
                  type="text"
                  name="company_name"
                  value={formData.company_name}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2"
                  style={{ focusRingColor: COLORS.primary }}
                  placeholder="ZOBOX Protech"
                />
              </div>

              {/* WhatsApp Number */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  WhatsApp Business Number *
                </label>
                <input
                  type="tel"
                  name="phone_number"
                  value={formData.phone_number}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2"
                  placeholder="+919599900999"
                />
                <p className="text-xs text-gray-500 mt-1">Include country code (e.g., +91)</p>
              </div>

              {/* Welcome Message */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Welcome Message
                </label>
                <textarea
                  name="welcome_message"
                  value={formData.welcome_message}
                  onChange={handleInputChange}
                  rows="3"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2"
                  placeholder="Hi! How can we help you today?"
                />
              </div>

              {/* Agent Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Agent/Team Name
                </label>
                <input
                  type="text"
                  name="agent_name"
                  value={formData.agent_name}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2"
                  placeholder="Support Team"
                />
              </div>

              {/* Business Hours */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Business Hours
                </label>
                <input
                  type="text"
                  name="business_hours"
                  value={formData.business_hours}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2"
                  placeholder="Mon-Fri 9AM-6PM"
                />
              </div>

              {/* Button Text */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Button Text
                </label>
                <input
                  type="text"
                  name="button_text"
                  value={formData.button_text}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2"
                  placeholder="Chat with us"
                />
              </div>

              {/* Position */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Widget Position
                </label>
                <select
                  name="position"
                  value={formData.position}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2"
                >
                  <option value="bottom-right">Bottom Right</option>
                  <option value="bottom-left">Bottom Left</option>
                </select>
              </div>

              {/* Primary Color */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Primary Color
                </label>
                <div className="flex gap-2">
                  <input
                    type="color"
                    name="primary_color"
                    value={formData.primary_color}
                    onChange={handleInputChange}
                    className="w-16 h-10 border border-gray-300 rounded cursor-pointer"
                  />
                  <input
                    type="text"
                    value={formData.primary_color}
                    onChange={(e) => setFormData(prev => ({ ...prev, primary_color: e.target.value }))}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2"
                  />
                </div>
              </div>

              {/* Active Status */}
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  name="is_active"
                  checked={formData.is_active}
                  onChange={handleInputChange}
                  className="w-5 h-5 text-green-600"
                />
                <label className="text-sm font-medium text-gray-700">
                  Widget is Active
                </label>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={saving}
                className="w-full px-6 py-3 text-white rounded-lg font-semibold hover:opacity-90 disabled:opacity-50"
                style={{ backgroundColor: COLORS.whatsapp }}
              >
                {saving ? '💾 Saving...' : '💾 Save Configuration'}
              </button>
            </form>
          </div>

          {/* Embed Code & Preview */}
          <div className="space-y-6">
            {/* Embed Code */}
            {embedCode && (
              <div className="bg-white rounded-lg shadow-lg p-6">
                <h2 className="text-2xl font-semibold mb-4" style={{ color: COLORS.primary }}>
                  📋 Embed Code
                </h2>
                <p className="text-sm text-gray-600 mb-4">
                  Copy and paste this code before the closing &lt;/body&gt; tag on your website
                </p>
                <div className="relative">
                  <pre className="bg-gray-900 text-green-400 p-4 rounded-lg overflow-x-auto text-sm">
                    {embedCode}
                  </pre>
                  <button
                    onClick={copyEmbedCode}
                    className="absolute top-2 right-2 px-3 py-1 bg-green-500 text-white rounded text-sm hover:bg-green-600"
                  >
                    📋 Copy
                  </button>
                </div>
              </div>
            )}

            {/* Preview */}
            <div className="bg-white rounded-lg shadow-lg p-6">
              <h2 className="text-2xl font-semibold mb-4" style={{ color: COLORS.primary }}>
                👁️ Live Preview
              </h2>
              <div className="bg-gray-100 rounded-lg p-4 h-96 relative">
                <p className="text-center text-gray-500 mb-4">
                  Widget Preview (Bottom {formData.position === 'bottom-right' ? 'Right' : 'Left'})
                </p>
                
                {/* Simple Preview */}
                <div 
                  className="absolute"
                  style={{
                    bottom: '20px',
                    [formData.position === 'bottom-right' ? 'right' : 'left']: '20px'
                  }}
                >
                  <button
                    className="px-6 py-3 rounded-full text-white font-medium shadow-lg flex items-center gap-2"
                    style={{ backgroundColor: formData.primary_color }}
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="white">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                    </svg>
                    {formData.button_text}
                  </button>
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-4 text-center">
                💡 Install the widget on your website to see the full interactive version
              </p>
            </div>

            {/* Instructions */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
              <h3 className="text-lg font-semibold mb-3 text-blue-900">📖 Installation Instructions</h3>
              <ol className="space-y-2 text-sm text-blue-800">
                <li>1. ✅ Configure your widget settings above</li>
                <li>2. 💾 Click "Save Configuration"</li>
                <li>3. 📋 Copy the embed code</li>
                <li>4. 🌐 Paste it before &lt;/body&gt; tag on your website</li>
                <li>5. 🎉 Your WhatsApp chat widget is live!</li>
              </ol>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}

export default WhatsAppWidget;
